import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/sharp-light-svg-icons";
import { faCircleX } from "@fortawesome/sharp-regular-svg-icons";
import { Disclosure } from "@headlessui/react";
import type { ReactNode } from "react";
import type { EmissionRecordView, Form } from "../../../../apiClient/generated";
import { formatDate } from "../../../../utils/dates";
import type { SectionDefinition } from "../../../EventsV2/formDefinition";

/**
 * UI-only building blocks for FormV2. No hooks or data fetching here —
 * everything is driven by props.
 */

export const EventFormHeader = ({
    emissionRecord,
    requestCloseForm,
    actions,
}: {
    emissionRecord: EmissionRecordView;
    requestCloseForm: () => void;
    actions: ReactNode;
}) => {
    return (
        <div className="bg-[#153240] p-3 text-white text-xs">
            <div className="flex w-full items-center justify-between">
                <span>
                    {emissionRecord.providerName} (
                    {emissionRecord.dataPoint.detectedRate != null
                        ? (
                              emissionRecord.dataPoint.detectedRate / 1000
                          ).toFixed(0)
                        : "-"}{" "}
                    kg/h on{" "}
                    {formatDate(emissionRecord.dataPoint.detectionTimestamp)})
                </span>
                <button onClick={requestCloseForm} type="button">
                    <FontAwesomeIcon icon={faCircleX} className="h-4 w-4" />
                </button>
            </div>
            <div className="flex w-full items-center justify-between mt-4">
                <h1 className="text-lg">Event Management</h1>
                {actions}
            </div>
        </div>
    );
};

export const PillButton = ({
    icon,
    label,
    onClick,
    variant = "neutral",
}: {
    icon: IconDefinition;
    label: string;
    onClick: () => void;
    variant?: "neutral" | "ghost";
}) => (
    <button
        type="button"
        onClick={onClick}
        className={`rounded-full border px-4 py-1 flex items-center gap-2 text-xs font-medium shadow-sm transition-colors hover:border-blue-300 hover:text-blue-500 hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed ${
            variant === "neutral"
                ? "border-neutral-200 bg-white text-neutral-500"
                : "border-transparent bg-transparent text-neutral-500"
        }`}
    >
        <FontAwesomeIcon icon={icon} />
        {label}
    </button>
);

export const FormNavigationPills = ({
    forms,
    groupsWithErrors,
    onSelectForm,
}: {
    forms: {
        form: Form;
        remove?: () => void;
    }[];
    groupsWithErrors: Set<string>;
    onSelectForm: (formName: string) => void;
}) => {
    if (forms.length === 0) return null;

    return (
        <div className="flex items-center gap-1.5 flex-wrap px-2 pb-2">
            {forms.map(({ form, remove }) => (
                <div key={form.id} className="inline-flex">
                    <div
                        className={`flex items-stretch rounded-full border bg-white shadow-sm text-xs font-medium overflow-hidden ${
                            groupsWithErrors.has(form.name)
                                ? "border-red-100"
                                : "border-neutral-200"
                        }`}
                    >
                        {/* Select button */}
                        <button
                            type="button"
                            onClick={() => onSelectForm(form.name)}
                            className={`flex items-center gap-1.5 px-3 py-1 transition-colors ${
                                groupsWithErrors.has(form.name)
                                    ? "text-red-500 hover:bg-red-50"
                                    : "text-neutral-600 hover:text-blue-500 hover:bg-blue-50"
                            }`}
                        >
                            {form.name}
                            {groupsWithErrors.has(form.name) && (
                                <FontAwesomeIcon
                                    icon={faTriangleExclamation}
                                    className="h-3 w-3 text-red-400"
                                />
                            )}
                        </button>

                        {/* Remove button, only if remove exists */}
                        {!!remove && (
                            <>
                                <div
                                    className={`w-px self-stretch ${
                                        groupsWithErrors.has(form.name)
                                            ? "bg-red-100"
                                            : "bg-neutral-200"
                                    }`}
                                />
                                <button
                                    type="button"
                                    title="Remove form section"
                                    onClick={() => {
                                        if (
                                            confirm(
                                                "Are you sure you want to remove this form section? Any data SAVED on those fields will be lost.",
                                            )
                                        ) {
                                            remove();
                                        }
                                    }}
                                    className={`flex items-center px-1.5 transition-colors ${
                                        groupsWithErrors.has(form.name)
                                            ? "text-red-400 hover:bg-red-50"
                                            : "text-neutral-400 hover:text-red-500 hover:bg-red-50"
                                    }`}
                                >
                                    <FontAwesomeIcon
                                        icon={faXmark}
                                        className="h-2.5 w-2.5"
                                    />
                                </button>
                            </>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export const FormGroupSection = ({
    group,
    index,
    hasError,
    sectionRef,
    children,
    defaultOpen,
}: {
    group: SectionDefinition;
    index: number;
    defaultOpen: boolean;
    hasError: boolean;
    sectionRef: (el: HTMLDivElement | null) => void;
    children: ReactNode;
}) => (
    <Disclosure defaultOpen={defaultOpen}>
        {({ open }) => (
            <div ref={sectionRef}>
                <Disclosure.Button
                    className={`w-full flex items-center justify-between gap-2 pb-2 py-2 px-3 text-left text-base font-medium transition-colors hover:bg-ae-slate-100 focus:outline-none focus-visible:text-blue-600 ${
                        hasError
                            ? "text-red-500"
                            : "text-neutral-900 hover:text-blue-600"
                    }`}
                >
                    <span className="flex items-center gap-2">
                        {index + 1}. {group.groupName}
                        {hasError && (
                            <FontAwesomeIcon
                                icon={faTriangleExclamation}
                                className="h-3.5 w-3.5 text-red-400"
                            />
                        )}
                    </span>
                    <FontAwesomeIcon
                        icon={faChevronDown}
                        className={`h-3.5 w-3.5 transition-transform duration-200 ${
                            hasError ? "text-red-300" : "text-neutral-500"
                        } ${open ? "" : "-rotate-90"}`}
                    />
                </Disclosure.Button>
                <Disclosure.Panel className="py-2 px-3 flex flex-col gap-3">
                    {children}
                </Disclosure.Panel>
            </div>
        )}
    </Disclosure>
);
